import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Container, Paper, Avatar, Typography, Tabs, Tab, Box, Divider } from '@mui/material';
import UserDetails from './components/UserDetails/UserDetails';
import AvatarSection from './components/AvatarSection';
import UserRates from './components/UserRates/UserRates';
import UserPortfolio from './components/UserPortfolio/UserPortfolio';
import UserReviews from './components/UserReviews/UserReviews';
import { HandymanAppState } from '../../configuration/redux/reducers';
import GoogleMapsWrapper from './components/UserDetails/GoogleMapsWrapper';


interface TabPanelProps {
    children?: React.ReactNode;
    value: number;
    index: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
    return (
        <div role="tabpanel" hidden={value !== index}>
            {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
        </div>
    );
};

interface User {
    avatar: string;
    name: string;
    email: string;
    location: string;
    birthDate: string;
    gender: string;
    bio: string;
    id: number;
}

interface UserProfileProps {}

enum UserProfileTabs {
    ContactInfo = 1,
    Portfolio = 2,
    Rates = 3,
    Reviews = 4,
}

const UserProfile: React.FC<UserProfileProps> = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const currentUser = useSelector((state: HandymanAppState) => state.user.currentUserDetails);

    const getActiveTabFromUrl = (): UserProfileTabs => {
        const params = new URLSearchParams(location.search);
        const tab = parseInt(params.get('activeTab') || '1', 10);
        return Object.values(UserProfileTabs).includes(tab) ? tab : UserProfileTabs.ContactInfo;
    };

    const [activeTab, setActiveTab] = useState<UserProfileTabs>(getActiveTabFromUrl());

    useEffect(() => {
        const currentTab = getActiveTabFromUrl();
        if (currentTab !== activeTab) {
            setActiveTab(currentTab);
        }
    }, [location.search]);

    const handleChange = (event: React.SyntheticEvent, newValue: UserProfileTabs) => {
        setActiveTab(newValue);
        navigate(`/profile?activeTab=${newValue}`);
    };

    return (
        <Container maxWidth="lg" style={{ marginTop: '7rem' }}>
            <Paper elevation={3} style={{ padding: '2rem' }}>
                <AvatarSection />

                <Divider sx={{ my: 2 }} />

                <Tabs value={activeTab} onChange={handleChange} centered>
                    <Tab label="General Information" value={UserProfileTabs.ContactInfo} />
                    <Tab label="Portfolio" value={UserProfileTabs.Portfolio} />
                    <Tab label="Rates" value={UserProfileTabs.Rates} />
                    <Tab label="Reviews" value={UserProfileTabs.Reviews} />
                </Tabs>

                <TabPanel value={activeTab} index={UserProfileTabs.ContactInfo}>
                    <GoogleMapsWrapper>
                        <UserDetails />
                    </GoogleMapsWrapper>
                </TabPanel>

                <TabPanel value={activeTab} index={UserProfileTabs.Portfolio}>
                    <UserPortfolio />
                </TabPanel>

                <TabPanel value={activeTab} index={UserProfileTabs.Rates}>
                    <UserRates />
                </TabPanel>

                <TabPanel value={activeTab} index={UserProfileTabs.Reviews}>
                    <UserReviews userId={currentUser?.id} />
                </TabPanel>
            </Paper>
        </Container>
    );
};

export default UserProfile;
